/**
 * React Query Hook for Experiences
 *
 * Custom hook for fetching experiences using React Query
 */

import { useQuery } from '@tanstack/react-query';
import {
  fetchFeaturedExperiences,
  fetchSignatureJourneys,
  fetchCuratedPackages,
  fetchBoutiqueStays,
  fetchGuidedActivities,
  fetchRidersCorner,
  fetchExperienceStats,
  fetchExperiences,
  ExperienceRequest,
} from '@/services/experienceService';
import { useAuthTokens } from '@/utils/tokenHelper';

/**
 * Hook to fetch featured experiences for landing page (6 items)
 * Automatically uses the access token and refresh token from auth store
 */
export function useFeaturedExperiences() {
  const { accessToken, refreshToken } = useAuthTokens();

  return useQuery({
    queryKey: ['experiences', 'featured', accessToken, refreshToken],
    queryFn: () =>
      fetchFeaturedExperiences(
        accessToken || undefined,
        refreshToken || undefined
      ),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: 2, // Retry failed requests 2 times
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
}

/**
 * Hook to fetch experiences with custom filters
 * Automatically uses the access token and refresh token from auth store
 * Caching disabled to ensure fresh data on every request
 */
export function useExperiences(
  request: ExperienceRequest = {},
  enabled = true
) {
  const { accessToken, refreshToken } = useAuthTokens();

  return useQuery({
    queryKey: ['experiences', request, accessToken, refreshToken],
    queryFn: () =>
      fetchExperiences(
        request,
        accessToken || undefined,
        refreshToken || undefined
      ),
    enabled,
    staleTime: 0, // No caching - always fetch fresh data
    gcTime: 0, // No garbage collection time - remove from cache immediately
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnWindowFocus: true, // Always refetch when window regains focus
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to fetch signature journeys for landing page (3 items)
 * Automatically uses the access token and refresh token from auth store
 */
export function useSignatureJourneys() {
  const { accessToken, refreshToken } = useAuthTokens();

  return useQuery({
    queryKey: ['experiences', 'signature-journeys', accessToken, refreshToken],
    queryFn: () =>
      fetchSignatureJourneys(
        accessToken || undefined,
        refreshToken || undefined
      ),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to fetch curated packages for landing page (up to 50 items, displayed as 10 on landing page)
 * Automatically uses the access token and refresh token from auth store
 */
export function useCuratedPackages() {
  const { accessToken, refreshToken } = useAuthTokens();

  return useQuery({
    queryKey: ['experiences', 'curated-packages', accessToken, refreshToken],
    queryFn: () =>
      fetchCuratedPackages(accessToken || undefined, refreshToken || undefined),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to fetch boutique stays for landing page (up to 20 items, displayed as 10 on landing page)
 * Automatically uses the access token and refresh token from auth store
 */
export function useBoutiqueStays() {
  const { accessToken, refreshToken } = useAuthTokens();

  return useQuery({
    queryKey: ['experiences', 'boutique-stays', accessToken, refreshToken],
    queryFn: () =>
      fetchBoutiqueStays(accessToken || undefined, refreshToken || undefined),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to fetch guided activities for landing page (3 items)
 * Automatically uses the access token and refresh token from auth store
 */
export function useGuidedActivities() {
  const { accessToken, refreshToken } = useAuthTokens();

  return useQuery({
    queryKey: ['experiences', 'guided-activities', accessToken, refreshToken],
    queryFn: () =>
      fetchGuidedActivities(
        accessToken || undefined,
        refreshToken || undefined
      ),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to fetch riders corner for landing page (2 items)
 * Automatically uses the access token and refresh token from auth store
 */
export function useRidersCorner() {
  const { accessToken, refreshToken } = useAuthTokens();

  return useQuery({
    queryKey: ['experiences', 'riders-corner', accessToken, refreshToken],
    queryFn: () =>
      fetchRidersCorner(accessToken || undefined, refreshToken || undefined),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook to fetch experience statistics for About Us section
 * Automatically uses the access token and refresh token from auth store
 */
export function useExperienceStats() {
  const { accessToken, refreshToken } = useAuthTokens();

  return useQuery({
    queryKey: ['experiences', 'stats', accessToken, refreshToken],
    queryFn: () =>
      fetchExperienceStats(accessToken || undefined, refreshToken || undefined),
    staleTime: 10 * 60 * 1000, // 10 minutes (stats change less frequently)
    gcTime: 20 * 60 * 1000, // 20 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
